import { parseEnvironment } from '@goldcast/utils';

import devConfig from './devConfig';

export const AGORA = {
  get APP_ID() {
    return getEnv('VUE_APP_AGORA_APP_ID');
  },
  ROLE: {
    SPEAKER: 'host',
    ATTENDEE: 'audience'
  },
  MODE: {
    RTC: 'rtc',
    LIVE: 'live'
  },
  NETWORK_ERRORS: {
    TOO_MANY_RESUBSCRIPTIONS: 'TOO_MANY_RESUBSCRIPTIONS',
    CLIENT_JOIN_FAILED: 'CLIENT_JOIN_FAILED',
    STUCK_AT_JOIN: 'STUCK_AT_JOIN',
    STREAM_PUBLISH_FAILED: 'STREAM_PUBLISH_FAILED',
    NO_ICE_CANDIDATE: 'NO_ICE_CANDIDATE',
    ERR_REPEAT_JOIN: 'ERR_REPEAT_JOIN'
  },
  TEST_CHANNEL: '53635e1a-35b6-11ed-a261-0242ac120002',
  CAPACITY_CHECK_DELAY: 1000,
  NOTIFICATION_TIMEOUT: 15000,
  GENERAL_NOTIFICATION_TIMEOUT: 5000,
  AV_NOTIFICATION_TIMEOUT: 3000,
  SHOW_AV_NOTIFICATIONS: false,
  UID_SUFFIX: {
    TV_SCREEN: '|tv-screen',
    LIVE_STREAMER: '|live-stream',
    BACKSTAGE: '|backstage',
    SCREEN_STREAM: '|shared-screen-stream'
  },
  VIDEO_QUALITY: [
    { label: 'Auto', name: 'AUTO', value: '480p_8' },
    { label: '180p', name: 'LOWEST', value: '180p_1' },
    { label: '240p', name: 'LOW', value: '240p_4' },
    { label: '360p', name: 'MEDIUM', value: '360p_9' },
    { label: '480p', name: 'REGULAR', value: '480p_8' },
    { label: '720p', name: 'HIGH', value: '720p_1' },
    { label: '1080p', name: 'ULTRA_HIGH', value: '1080p_1' }
  ],

  GOOD_FPS: 15,
  /**
   * If frames fall under this threshold, we can assume there is a problem
   * with their network or their cpu is being choked.
   */
  FPS_FALL_THRESHOLD: 8,
  RECORDING_STUDIO_USER_LIMIT: 8
};

export const MATTERMOST_DEFAULTS = {
  EXCLUDED_ROOM_ID: '00000000000000000000000000',
  EXCLUDED_ROUTE_NAMES: ['EventSplash']
};

/** @returns {string[]} */
export function getBotMmids() {
  return getEnv('VUE_APP_MATTERMOST_BOT_MMIDS').split(',').filter(Boolean);
}

/** @type {import('@goldcast/utils').ParsedEnvironment | null} */
let _parsedEnv = null;
/** @returns {import('@goldcast/utils').ParsedEnvironment} */
export function getAppEnvironment() {
  if (!_parsedEnv) {
    _parsedEnv = parseEnvironment(getEnv('VUE_APP_CVENT_ENV') || 'local');
  }
  return _parsedEnv;
}

export const EMBEDDED_SESSION_STORAGE_KEY = 'isEmbeddedFlow';

export const S3_PREFIX = {
  EVENT_LOGOS: 'eventLogos',
  RESOURCE: 'resource',
  EVENT_BANNER_IMAGES: 'eventBanner',
  USER_IMAGE: 'userImage'
};
export function getCloudFrontUrl(prefixKey) {
  const CLOUDFRONT = {
    CLOUDFRONT_URL: getEnv('VUE_APP_CLOUD_FRONT_URL'),
    USER_PROFILE_IMAGE_KEY_PREFIX: 'user'
  };
  return `${CLOUDFRONT[prefixKey]}`;
}

export const RESIZED_USER_IMAGE_S3_FOLDER = 'user-resized';
export const RESIZED_MEDIUM_USER_IMAGE_S3_FOLDER = 'user-resized-medium';

export const NETWORK_TIMEOUT = 5000;

export const LOCAL_STORAGE_KEYS = {
  techCheckedEvents: 'techCheckedEvents',
  userVirtualBgSpec: 'userVirtualBgSpec',
  isUserVideoMirrored: 'isUserVideoMirrored',
  attendeeTechCheck: '__attendee__tech__check',
  userSurveySubmissionKey: '__userSurveySubmissionKey',
  speakerTechCheck: '__speaker__tech__check',
  selectedVideoQuality: 'selected_video_quality',
  noiseCancellationLevel: 'noise_cancellation_level',
  noiseCancellationEnabled: 'noise_cancellation_enabled',
  preferredCloudProxyMode: 'preferredCloudProxyMode',
  preferredTurnServerUrl: 'preferredTurnServerUrl',
  hasShownJoiningDelayNetworkErrorModal: 'hasShownJoiningDelayNetworkErrorModal'
};

export const HLS_PLAYBACK = {
  HLS_PLAYBACK_MAX_FALLBACK_DURATION: 12000,
  HLS_PLAYBACK_SYNC_INTERVAL: 6000,
  FRAG_LOAD_MAX_RETRIES: 6,
  MANIFEST_LOAD_MAX_RETRIES: 1,
  LEVEL_LOAD_MAX_RETIRES: 4,
  WAIT_TIME_BEFORE_REFRESHING_ROOM_ON_STREAM_END: 10000,
  WAIT_TIME_BEFORE_ALLOWING_NEXT_VIDEO_PLAYBACK_SELECTION: 60 * 1000,
  WAIT_TIME_BEFORE_SHOWING_PLAYBACK_STALL_INDICATOR: 2000,
  WAIT_TIME_FOR_MANIFEST_LOADING_RETRIES: 3000,
  WAIT_TIME_BEFORE_NUDGING_PLAYBACK_FORWARD: 2000,
  MAX_ALLOWED_FALL_BEHIND_DURATION: 36 * 1000,
  MAX_ALLOWED_TIME_FOR_VIDEO_PLAYBACK_AFTER_SESSION: 2 * 60 * 1000,
  BROADCAST_VIDEO_PLAYBACK_FORCED_END_DURATION: 10 * 60 * 1000,
  HLS_ALLOWED_PLAY_AHEAD_DURATION: 4 * 1000
};

if (import.meta.env.MODE === 'development') {
  window.configs = devConfig;
}

/** @param {string} value @returns {string} */
export function getEnv(value) {
  let name = value;
  const v2Name = `${name}_v2`;
  const isV2UrlAvailable = window?.configs?.[v2Name];
  if (isV2UrlAvailable) {
    name = v2Name;
  }

  const result = window?.configs?.[name];
  if (!result) {
    console.error(`[CONFIG] Missing environment variable: ${name}. This may cause unexpected behavior.`);
    return '';
  }
  return result;
}

/** @param {string} [path] @returns {string} */
export function getStaticAssetsUrl(path) {
  const base = getEnv('VUE_APP_STATIC_ASSETS_BASE_URL').replace(/\/+$/, '');
  return path ? `${base}/${path}` : `${base}/`;
}

/** @returns {string[]} */
export function getRecorderEmails() {
  const raw = getEnv('VUE_APP_RECORDER_EMAILS');
  if (!raw) return [];
  return raw
    .split(',')
    .map(e => e.trim())
    .filter(Boolean);
}

export const BUFFER_TIME_AFTER_SIMULIVE_TESTING_ENDS = 2 * 60 * 1000;
export const MIN_SIMULIVE_PLAYBACK_START_DELAY = 12 * 1000;
export const LIVE_HYBRID_EXPECTED_PLAYBACK_DELAY = 2 * 1000;

export const HEARTBEAT_INTERVAL_TIME = 1000;
export const VIDEO_BROADCAST_TIME_INTERVAL_WINDOW_FOR_ACTIVITY_LOGS = 4 * HEARTBEAT_INTERVAL_TIME;

export const FORCE_LIVE_VIDEO_PLAYBACK_START_WINDOW = 3 * HEARTBEAT_INTERVAL_TIME;

export const BROADCAST_END_ALLOWANCE_TIME = 30 * 60 * 1000;

export const helpDocLinks = {
  ROOT_HELP_DOC_LINK: 'https://help.goldcast.io/hc/en-us',
  RS_TECH_CHECK_HELP_DOC_LINK: 'https://help.goldcast.io/recording-studio-system-check-troubleshooting',
  RS_HELP_DOC_LINK: 'https://help.goldcast.io/recording-studio',
  ATTENDEE_TECH_CHECK_HELP_DOC_LINK:
    'https://help.goldcast.io/troubleshooting/9223491550875-troubleshooting-audio-issues',
  SCREEN_SHARE_TEST: 'https://help.goldcast.io/troubleshooting/9191758014875-troubleshooting-screen-sharing-issues',
  CAMERA_MIC_SETUP: 'https://help.goldcast.io/hc/en-us/articles/9190580004763',
  SPEAKER_TECH_SETUP_CHECK: 'https://help.goldcast.io/hc/en-us/articles/9188871212699',
  AUDIO_SETTINGS: 'https://help.goldcast.io/hc/en-us/articles/9223491550875',
  CONNECTION_SETTINGS:
    'https://help.goldcast.io/hc/en-us/articles/4408141111323-Configuring-Your-Connection-Settings-Network-VPN-and-Firewall',
  TROUBLESHOOTING_CONNECTING_GOLDCAST_EVENT:
    'https://help.goldcast.io/en_US/troubleshooting/troubleshooting-connecting-goldcast-event',
  RECORDING_STUDIO_SYSTEM_REQUIREMENTS:
    'https://help.goldcast.io/en_US/recording-studio/recording-studio-system-requirements'
};

export const NOISE_SUPPRESSION_DROPDOWN_OPTIONS = [
  {
    label: 'Soft (Light level of noise suppression)',
    value: 'SOFT',
    id: 'SOFT'
  },
  {
    label: 'Aggressive (High suppression, could affect voice quality)',
    value: 'AGGRESSIVE',
    id: 'AGGRESSIVE'
  }
];

export const RECORDING_STUDIO_STATE = {
  RECORDING: 'RECORDING',
  STOPPED: 'STOPPED',
  NOT_INITIALIZED: 'NOT_INITIALIZED',
  STARTING: 'STARTING',
  PAUSED: 'PAUSED'
};

export const RECORDING_STATE_PRETTY_NAME_MAP = {
  NOT_INITIALIZED: 'Not Started',
  RECORDING: 'Recording',
  STOPPED: 'Ended',
  PAUSED: 'Paused'
};

export const AGORA_EVENTS = {
  PEER_CONNECTION_STATE_CHANGE: 'peerconnection-state-change'
};

export const AGORA_CONNECTION_TIMEOUT = 10000;

export const SEARCH_COLUMN_FULLNAME = 'fullname';
export const DEFAULT_ORDER_BY = '-created_at';
export const ORDER_BY_RANDOM = 'random';
export const FILTER_OPERATOR_CONSTANTS = {
  EQUALS: 'EQUALS',
  ICONTAINS: 'ICONTAINS'
};

export const MIN_RECORD_LATER_BUFFER_MS = 6 * 60 * 1000; // 6 minutes
